import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, MovieClip, View } from '@play-co/timestep-core/ui';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import buttons from 'src/lib/ui/config/buttons';
import Sticker, { STICKER_IDS } from '../components/Sticker';

/** Display the sticker campaign artwork and its nine animated previews. */
export default class PopupStickers extends PopupBasic {
  /** Previews whose initial playback starts after the popup assets are ready. */
  private readonly stickers: Sticker[];

  /**
   * Build the supplied layout without scheduling or navigating to a campaign.
   * @param opts Parent view and a close callback reporting download intent.
   */
  constructor(opts: { superview: View; close: (action?: 'download') => void }) {
    // Fit the reference canvas inside the screen while preserving its proportions.
    const scale = Math.min(
      680 / 1280,
      (opts.superview.style.width - 40) / 1280,
      (opts.superview.style.height - 40) / 1920,
    );
    super({
      superview: opts.superview,
      close: () => opts.close(),
      width: 1280 * scale,
      height: 1920 * scale,
      skipBackground: true,
      skipTitle: true,
      skipMessage: true,
      tracking: { feature: 'stickers', $subFeature: 'popup' },
    });

    // Use reference coordinates for the artwork and all of its overlays.
    const content = new View({
      superview: this.box,
      width: 1280,
      height: 1920,
      scale,
    });
    new ImageScaleView({
      superview: content,
      image: 'assets/ui/stickers/popup_ja.png',
      width: 1280,
      height: 1920,
    });

    // Reuse the popup's working close button at the reference position.
    this.buttonClose.updateOpts({
      x: 1188 * scale,
      y: 194 * scale,
      width: 150 * scale,
      height: 158 * scale,
    });

    // Place the large first preview above the remaining two rows of four.
    this.stickers = STICKER_IDS.map((stickerId, index) => {
      const featured = index === 0;
      return new Sticker({
        superview: content,
        stickerId,
        x: featured ? 180 : 170 + ((index - 1) % 4) * 245,
        y: featured ? 735 : 1080 + Math.floor((index - 1) / 4) * 185,
        width: featured ? 400 : 200,
        height: featured ? 325 : 162,
      });
    });

    // Display the campaign's November 9 end date in the player's locale.
    new LangBitmapFontTextView({
      superview: content,
      x: 150,
      y: 1520,
      width: 980,
      height: 70,
      font: 'Body',
      size: 44,
      color: '#FFFFFF',
      align: 'center',
      verticalAlign: 'center',
      localeText: () => i18n('stickers.campaignPeriod'),
    });

    // Report button intent to the caller; navigation is outside this popup's scope.
    new ButtonScaleViewWithText({
      ...buttons.secondary,
      superview: content,
      x: 295,
      y: 1610,
      width: 685,
      height: 175,
      font: 'Body',
      fontSize: 94,
      localeText: () => i18n('stickers.download'),
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close('download');
      },
      tracking: { feature: 'stickers', $subFeature: 'download' },
    });
  }

  /** @returns Artwork and converted animations required before revealing the popup. */
  protected getAssetsGroup(): AssetGroup {
    // Preload each MovieClip through its animation loader, not as a plain image.
    return new AssetGroup([
      'assets/ui/stickers/popup_ja.png',
      ...STICKER_IDS.map((id) => ({
        url: `assets/ui/stickers/movieclips/${id}`,
        loadMethod: MovieClip.animationLoader,
      })),
    ]);
  }

  /** Reveal the loaded popup before starting each sticker's initial playback. */
  public async fadeIn(): Promise<void> {
    // Do not consume the one-shot animations while the asset loader hides the box.
    await super.fadeIn();

    // Start every preview once on each opening, without tap-to-replay.
    for (const sticker of this.stickers) {
      sticker.playOnce();
    }
  }
}
